/**
 * <AdminPremium /> — CRUD panel for premium subscription tiers.
 *
 * Admins can create/edit/delete tiers and toggle active state. Each tier
 * stores its own Stripe price IDs (monthly + yearly) that the admin pastes
 * in from the Stripe dashboard.
 *
 * Also shows the current subscriber roster grouped by tier so the admin can
 * verify who's paying without touching Stripe.
 */
import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, Star, X, ExternalLink, Loader2, Users } from "lucide-react";

const EMPTY_TIER = {
  name: "",
  tagline: "",
  description: "",
  perks: [],
  price_display_monthly: "",
  price_display_yearly: "",
  stripe_price_id_monthly: "",
  stripe_price_id_yearly: "",
  currency: "usd",
  active: true,
  sort_order: 100,
  highlight: false,
  badge: "",
};

export default function AdminPremium() {
  const [tiers, setTiers] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // tier object or "new"

  const load = async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        http.get("/admin/premium/tiers"),
        http.get("/admin/premium/subscribers"),
      ]);
      setTiers(t.data.tiers || []);
      setSubscribers(s.data.subscribers || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load premium data.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async (draft) => {
    try {
      if (draft.id) {
        await http.patch(`/admin/premium/tiers/${draft.id}`, draft);
        toast.success("Tier updated.");
      } else {
        await http.post("/admin/premium/tiers", draft);
        toast.success("Tier created.");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save tier.");
    }
  };

  const toggle = async (t) => {
    try {
      await http.post(`/admin/premium/tiers/${t.id}/toggle`);
      load();
    } catch { toast.error("Couldn't toggle tier."); }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? Existing subscribers keep access until cancellation.`)) return;
    try {
      await http.delete(`/admin/premium/tiers/${t.id}`);
      toast.success("Tier deleted.");
      load();
    } catch { toast.error("Couldn't delete tier."); }
  };

  return (
    <div data-testid="admin-premium" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline" style={{ color: "var(--wofy-primary)" }}>
            <Star size={12} className="inline -mt-0.5 mr-1" /> PREMIUM TIERS
          </div>
          <h1 className="font-display text-3xl mt-1">Subscription plans</h1>
          <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>
            Create the plans users see on <b>/pricing</b>. Paste your Stripe monthly/yearly Price IDs
            for each tier (created once in the Stripe dashboard). Toggle plans on and off any time.
          </p>
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY_TIER })}
          className="wofy-btn-primary"
          data-testid="premium-new-tier"
        >
          <Plus className="w-4 h-4" /> New tier
        </button>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : tiers.length === 0 ? (
        <div className="wofy-card text-sm text-center py-10" style={{ color: "var(--wofy-text-soft)" }}>
          No plans yet. Click <b>New tier</b> above to create your first subscription plan.
          <div className="mt-4 text-xs">
            To get Stripe Price IDs: open <a className="underline" target="_blank" rel="noopener noreferrer" href="https://dashboard.stripe.com/test/products">Stripe Dashboard → Products <ExternalLink className="inline w-3 h-3" /></a>, create a product with monthly + yearly recurring prices, and paste the two `price_...` IDs here.
          </div>
        </div>
      ) : (
        <div className="grid gap-4">
          {tiers.map((t) => (
            <TierCard key={t.id} tier={t} onEdit={() => setEditing(t)} onToggle={() => toggle(t)} onDelete={() => del(t)} />
          ))}
        </div>
      )}

      <div>
        <h2 className="font-display text-xl mt-8 mb-3">
          <Users className="inline -mt-1 w-5 h-5 mr-2" />
          Active subscribers ({subscribers.length})
        </h2>
        {subscribers.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Nobody's subscribed yet.</p>
        ) : (
          <div className="wofy-card">
            <table className="w-full text-sm" data-testid="premium-subscribers-table">
              <thead>
                <tr className="text-left border-b" style={{ borderColor: "var(--wofy-line)" }}>
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Email</th>
                  <th className="py-2 pr-3">Tier</th>
                  <th className="py-2 pr-3">Interval</th>
                  <th className="py-2 pr-3">Started</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.map((u) => (
                  <tr key={u.id} className="border-b last:border-0" style={{ borderColor: "var(--wofy-line)" }} data-testid={`subscriber-row-${u.id}`}>
                    <td className="py-2 pr-3">{u.name}</td>
                    <td className="py-2 pr-3 opacity-70">{u.email}</td>
                    <td className="py-2 pr-3">{u.premium?.tier_name || "-"}</td>
                    <td className="py-2 pr-3">{u.premium?.interval || "-"}</td>
                    <td className="py-2 pr-3 opacity-70">{u.premium?.started_at ? new Date(u.premium.started_at).toLocaleDateString() : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <TierEditor
          initial={editing}
          allTiers={tiers}
          onCancel={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function TierCard({ tier, onEdit, onToggle, onDelete }) {
  return (
    <div
      className="wofy-card grid grid-cols-[1fr_auto] gap-4"
      style={{ opacity: tier.active ? 1 : 0.5 }}
      data-testid={`premium-tier-${tier.id}`}
    >
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-display text-lg">{tier.name}</h3>
          {tier.highlight && <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full" style={{ background: "var(--wofy-primary)", color: "white" }}>Highlighted</span>}
          {tier.badge && <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border" style={{ borderColor: "var(--wofy-line)" }}>{tier.badge}</span>}
          <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${tier.active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
            {tier.active ? "Active" : "Inactive"}
          </span>
        </div>
        {tier.tagline && <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>{tier.tagline}</p>}
        <div className="flex gap-4 mt-2 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
          <span>Monthly: <b>{tier.price_display_monthly || "—"}</b> {tier.stripe_price_id_monthly ? "✓" : <span className="text-red-500">no price ID</span>}</span>
          <span>Yearly: <b>{tier.price_display_yearly || "—"}</b> {tier.stripe_price_id_yearly ? "✓" : <span className="text-red-500">no price ID</span>}</span>
        </div>
        {(tier.perks || []).length > 0 && (
          <ul className="text-xs mt-2 grid grid-cols-2 gap-x-4 gap-y-1" style={{ color: "var(--wofy-text)" }}>
            {tier.perks.slice(0, 8).map((p, i) => <li key={i}>• {p}</li>)}
            {tier.perks.length > 8 && <li className="opacity-60">+ {tier.perks.length - 8} more…</li>}
          </ul>
        )}
      </div>
      <div className="flex flex-col gap-1 items-end">
        <button onClick={onEdit} className="wofy-btn-ghost" data-testid={`premium-edit-${tier.id}`}><Pencil className="w-3.5 h-3.5" /> Edit</button>
        <button onClick={onToggle} className="wofy-btn-ghost" data-testid={`premium-toggle-${tier.id}`}>
          {tier.active ? <><EyeOff className="w-3.5 h-3.5" /> Deactivate</> : <><Eye className="w-3.5 h-3.5" /> Activate</>}
        </button>
        <button onClick={onDelete} className="wofy-btn-ghost text-red-600" data-testid={`premium-delete-${tier.id}`}><Trash2 className="w-3.5 h-3.5" /> Delete</button>
      </div>
    </div>
  );
}

function TierEditor({ initial, onCancel, onSave, allTiers = [] }) {
  const [draft, setDraft] = useState({ ...initial });
  const [perkInput, setPerkInput] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  // Candidate tiers for downgrade target: any OTHER active, non-deleted tier.
  const downgradeCandidates = allTiers.filter((t) => t.id !== draft.id && t.active && !t.deleted);
  const addPerk = () => {
    const p = perkInput.trim();
    if (!p) return;
    set("perks", [...(draft.perks || []), p]);
    setPerkInput("");
  };
  const removePerk = (i) => set("perks", draft.perks.filter((_, x) => x !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!draft.name.trim()) { toast.error("Tier name is required."); return; }
    setBusy(true);
    try { await onSave(draft); }
    finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onCancel}
      data-testid="premium-editor"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl p-6 space-y-4"
        style={{ background: "var(--wofy-bg)", border: "1px solid var(--wofy-border)" }}
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl">{draft.id ? "Edit tier" : "New tier"}</h2>
          <button type="button" onClick={onCancel} className="wofy-btn-ghost" data-testid="premium-editor-close"><X className="w-4 h-4" /></button>
        </div>

        <Field label="Plan name *"><input required data-testid="tier-name" value={draft.name} onChange={(e) => set("name", e.target.value)} className="wofy-input" placeholder="e.g. Supporter" /></Field>
        <Field label="Tagline"><input data-testid="tier-tagline" value={draft.tagline || ""} onChange={(e) => set("tagline", e.target.value)} className="wofy-input" placeholder="e.g. Support the mission and unlock supporter-only perks." /></Field>
        <Field label="Description"><textarea data-testid="tier-description" value={draft.description || ""} onChange={(e) => set("description", e.target.value)} rows={3} className="wofy-input" /></Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Display price · monthly"><input data-testid="tier-price-monthly" value={draft.price_display_monthly || ""} onChange={(e) => set("price_display_monthly", e.target.value)} className="wofy-input" placeholder="$5 / month" /></Field>
          <Field label="Display price · yearly"><input data-testid="tier-price-yearly" value={draft.price_display_yearly || ""} onChange={(e) => set("price_display_yearly", e.target.value)} className="wofy-input" placeholder="$50 / year" /></Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Stripe Price ID · monthly"
            hint="From Stripe Dashboard → Products → your product. Starts with price_"
          >
            <input data-testid="tier-stripe-monthly" value={draft.stripe_price_id_monthly || ""} onChange={(e) => set("stripe_price_id_monthly", e.target.value)} className="wofy-input font-mono text-xs" placeholder="price_1234abc…" />
          </Field>
          <Field label="Stripe Price ID · yearly" hint="Optional — leave empty if you only offer monthly.">
            <input data-testid="tier-stripe-yearly" value={draft.stripe_price_id_yearly || ""} onChange={(e) => set("stripe_price_id_yearly", e.target.value)} className="wofy-input font-mono text-xs" placeholder="price_5678def…" />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Currency"><input value={draft.currency || "usd"} onChange={(e) => set("currency", e.target.value.toLowerCase())} maxLength={3} className="wofy-input uppercase" /></Field>
          <Field label="Sort order" hint="Lower = shown first."><input type="number" value={draft.sort_order || 100} onChange={(e) => set("sort_order", parseInt(e.target.value || 100, 10))} className="wofy-input" /></Field>
          <Field label="Badge (optional)"><input value={draft.badge || ""} onChange={(e) => set("badge", e.target.value)} maxLength={20} className="wofy-input" placeholder="MOST POPULAR" /></Field>
        </div>

        <div className="flex items-center gap-4 text-sm">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!draft.active} onChange={(e) => set("active", e.target.checked)} data-testid="tier-active" /> Plan is live
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!draft.highlight} onChange={(e) => set("highlight", e.target.checked)} data-testid="tier-highlight" /> Highlight on /pricing
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!draft.founding} onChange={(e) => set("founding", e.target.checked)} data-testid="tier-founding" /> Founding-member tier
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!draft.is_free} onChange={(e) => set("is_free", e.target.checked)} data-testid="tier-is-free" /> Free tier (baseline plan)
          </label>
        </div>
        {draft.founding && (
          <Field label="Founding tagline (shown on /pricing under the price)">
            <input value={draft.founding_tagline || ""} onChange={(e) => set("founding_tagline", e.target.value)} maxLength={120} className="wofy-input" placeholder="Lock in $30/year — founding members save $6 vs monthly." data-testid="tier-founding-tagline" />
          </Field>
        )}

        {/* Retention save offer — when a subscriber tries to cancel this tier,
            we show them a "Stay for less" CTA that switches them to the tier
            selected below. Admin picks the target tier (any active, cheaper
            one) and writes the marketing copy shown in the cancel modal. */}
        <div className="rounded-xl border p-3 space-y-3" style={{ borderColor: "var(--wofy-line)", background: "rgba(178,93,56,0.03)" }}>
          <div className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
            Retention save offer
            <span className="ml-1 opacity-60 normal-case">(shown when a subscriber tries to cancel this tier)</span>
          </div>
          <Field label="Downgrade target — offer this cheaper tier instead">
            <select
              value={draft.downgrade_target_tier_id || ""}
              onChange={(e) => set("downgrade_target_tier_id", e.target.value || null)}
              className="wofy-input"
              data-testid="tier-downgrade-target"
            >
              <option value="">— No save offer —</option>
              {downgradeCandidates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}{t.price_display_monthly ? ` · ${t.price_display_monthly}` : ""}
                </option>
              ))}
            </select>
          </Field>
          {draft.downgrade_target_tier_id && (
            <Field label="Save-offer copy" hint='e.g. "Same core features for half the price. Cancel anytime."'>
              <input
                value={draft.downgrade_offer_copy || ""}
                onChange={(e) => set("downgrade_offer_copy", e.target.value)}
                maxLength={160}
                className="wofy-input"
                placeholder="Same great community — for half the price."
                data-testid="tier-downgrade-copy"
              />
            </Field>
          )}
        </div>

        <Field label="Perks">
          <div className="flex flex-wrap gap-1 mb-2">
            {(draft.perks || []).map((p, i) => (
              <span key={i} className="text-xs rounded-full border px-2 py-1 inline-flex items-center gap-1" style={{ borderColor: "var(--wofy-line)" }}>
                {p}
                <button type="button" onClick={() => removePerk(i)} aria-label="Remove perk"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input value={perkInput} onChange={(e) => setPerkInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPerk(); } }} className="wofy-input flex-1" placeholder="e.g. Remove ads across the site" data-testid="tier-perk-input" />
            <button type="button" onClick={addPerk} className="wofy-btn-ghost" data-testid="tier-perk-add">Add</button>
          </div>
        </Field>

        <div className="flex justify-end gap-2 pt-4 border-t" style={{ borderColor: "var(--wofy-line)" }}>
          <button type="button" onClick={onCancel} className="wofy-btn-ghost">Cancel</button>
          <button type="submit" className="wofy-btn-primary" disabled={busy} data-testid="tier-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {draft.id ? "Save changes" : "Create tier"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>{label}</span>
      {children}
      {hint && <span className="block text-[10px] mt-0.5 opacity-60">{hint}</span>}
    </label>
  );
}
